import React, {Component} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios/index';
import {RESOURCES as inventoryResources} from '../fuseInventoryResourceMap';
import LocationAuditTableRow from '../components/LocationAuditTableRow';
import {
    convertDate,
    createDateTimeObject,
    generateUUID,
    getGetHeaders
} from '../shared/functions';
import {setSystemToLowercase} from '../shared/serverFunctions';
import {checkTokenStatus} from '../shared/tokenFunctions';
import Config from '../messages';
import FuseTheme from '../global/theme';
import {Card, CardHeader, CardText} from 'material-ui/Card';
import {
    Table,
    TableHeader,
    TableHeaderColumn,
    TableRow,
    TableRowColumn,
    TableBody
} from 'material-ui';
import Dialog from 'material-ui/Dialog';
import Snackbar from 'material-ui/Snackbar';
import FlatButton from 'material-ui/FlatButton';
import RaisedButton from 'material-ui/RaisedButton';
import CircularProgress from 'material-ui/CircularProgress';
import ContentChange from 'material-ui/svg-icons/content/create';
import ContentCheck from 'material-ui/svg-icons/action/check-circle';

/**
 * A component for retrieving and viewing Location Audit Contents (LocationAuditTableRows) and finalizing the Audit.
 *
 */
class LocationAuditTable extends Component {
    constructor(props) {
        super(props);
        this.state = {
            auditData: {},
            confirmationOpen: false,
            contentList: {},
            displayUpdate: false,
            errorMessage: '',
            noContents: true,
            noContentMessage: '',
            notificationMessage: '',
            notificationOpen: false,
            showErrorMessage: false,
            showSuccessMessage: false,
            submitting: false
        };
    }

    componentWillMount() {
        this.fetchLocationAuditContents();
    }

    /**
     * Get all contents for this Audit
     */
    fetchLocationAuditContents() {
        const auditContentParams = {
            filter: {
                location_id: this.props.location_id,
                audit_uuid: this.props.audit_uuid
            },
            pagination: {page: 1, perPage: 1000},
            sort: {field: 'audit_uuid', order: 'ASC'}
        };
        const auditContentResource = inventoryResources.LocationAuditContents.GET_LIST(auditContentParams);

        const tokenStatus = checkTokenStatus(null);
        if (tokenStatus.expired) {
            return;
        }

        // Retrieve this audit's contents data and store results
        axios({
            method: 'get',
            url: auditContentResource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data.payload) {
                // Set messaging and display based on payload
                let payload = result.data.payload;
                let contentList = null;
                let noContents = true;
                let noContentMessage = '';
                if (payload) {
                    if (!payload.length) {
                        // Convert single payload to an array for processing
                        contentList = [];
                        noContents = true;
                        if (payload.message) {
                            noContentMessage = payload.message;
                        }
                    } else {
                        contentList = payload;
                        noContents = false;
                        noContentMessage = null;
                        this.fetchLocationAuditData();
                    }
                }
                this.processLocationAuditContents(contentList, noContents, noContentMessage);
            } else {
                this.setState({
                    noContents: true
                });
            }
        }).catch((error) => {
            if (error.response) {
                // Server response outside 2xx
                console.log(error.response.data);
            } else if (error.request) {
                // No response
            } else {
                console.log('Error', error.message);
            }
        });
    }

    processLocationAuditContents(contentList, noContents, noContentMessage) {
        let list = [];
        if (contentList && contentList.length > 0) {
            list = contentList.filter(function (obj) {
                return obj.qty > 0;
            });
        }

        this.setState({
            contentList: list,
            noContents: noContents,
            noContentMessage: noContentMessage
        });
    }

    /**
     * Get data for this Audit
     */
    fetchLocationAuditData() {
        const auditParams = {
            filter: {
                id: this.props.location_id
            },
            pagination: {page: 1, perPage: 10},
            sort: {field: 'audit_uuid', order: 'ASC'}
        };
        const auditResource = inventoryResources.LocationAudits.GET_ONE(auditParams);

        const tokenStatus = checkTokenStatus(null);
        if (tokenStatus.expired) {
            return;
        }

        // Retrieve this audit's data and store results
        axios({
            method: 'get',
            url: auditResource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data.payload) {
                let payload = result.data.payload;
                let auditData = null;
                let auditList = null;
                if (payload) {
                    if (!payload.length) {
                        auditList = [payload];
                    } else {
                        auditList = payload;
                    }
                }
                for (let audit of auditList) {
                    if (audit.audit_uuid === this.props.audit_uuid) {
                        audit.date = convertDate(audit.start_time);
                        auditData = audit;
                        break;
                    }
                }
                this.setState({
                    auditData: auditData
                });
            }
        }).catch((error) => {
            if (error.response) {
                // Server response outside 2xx
                console.log(error.response.data);
            } else if (error.request) {
                // No response
            } else {
                console.log('Error', error.message);
            }
        });
    }

    // Handle event to call Update Audit functionality (currently unused)
    handleUpdateAudit = () => {
    };

    /**
     * Handle event to confirm Finalize Audit
     */
    handleShowConfirm = () => {
        this.setState({confirmationOpen: true});
    };

    /**
     * Call Finalize Audit functionality
     */
    handleFinalizeAudit = () => {

        // Assemble the data for an InventoryDomainModel
        const system = setSystemToLowercase();
        const email = localStorage.getItem('email');
        const auditData = this.state.auditData;
        const finalizeClientUdt = {
            system: system,
            location: auditData.location,
            register: Config.fuse.client.register,
            user: email,
            device: Config.fuse.client.device,
            version: Config.fuse.admin.version
        };
        const data = {
            audit_uuid: this.props.audit_uuid,
            created_by_client: auditData.created_by_client,
            finalized_by_client: finalizeClientUdt,
            location: auditData.location,
            mode: localStorage.getItem('mode'),
            signature: email,
            start_time: auditData.start_time,
            system: system,
            type: auditData.type,
            zero_day: auditData.zero_day
        };

        let payload = {
            'client': {
                system: localStorage.getItem('system'),
                location: Config.fuse.client.location,
                register: Config.fuse.client.register,
                user: localStorage.getItem('email'),
                device: Config.fuse.client.device,
                version: Config.fuse.admin.version
            },
            'payload': {
                'auditModel': data
            },
            'pkg_date_time': createDateTimeObject(),
            'pkg_uuid': generateUUID(),
            'pkg_version': 4
        };
        const resource = inventoryResources.LocationAuditFinalize.UPDATE();

        const successMessage = (auditData.type === 'final')
            ? 'Successfully finalized audit and closed this Location.'
            : 'Successfully finalized audit.';

        // Post the data
        this.setState({
            confirmationOpen: false,
            submitting: true
        }, () => {
            import ('../shared/serverFunctions').then(functions => {
                const tokenStatus = checkTokenStatus(null);
                if (tokenStatus.expired) {
                    return;
                }
                axios({
                    method: 'post',
                    url: resource.uri,
                    data: payload,
                    headers: functions.getPostHeaders()
                }).then((result) => {
                    if (result.data.payload) {
                        this.setState({
                            confirmationOpen: false,
                            notificationMessage: successMessage,
                            notificationOpen: true,
                            showErrorMessage: false,
                            showSuccessMessage: true,
                            submitting: false
                        }, () => {
                            setTimeout(
                                function () {
                                    this.props.history.push(this.props.listUrl);
                                }
                                    .bind(this),
                                3000
                            );
                        });
                    }
                }).catch((error) => {
                    let notificationMessage = 'Sorry, there was an error finalizing this Audit.';
                    let showErrorMessage = false;
                    if (error.response) {
                        // Server response outside 2xx
                        if (error.response.status && error.response.status !== 200) {
                            showErrorMessage = true;
                        }
                        if (error.response.data && error.response.data.error_message ) {
                            notificationMessage = error.response.data.error_message;
                            showErrorMessage = false;

                        }
                    } else if (error.request) {
                        // No response
                    } else {
                        console.log('Error ', error.message);
                    }
                    this.setState({
                        confirmationOpen: false,
                        notificationMessage: notificationMessage,
                        notificationOpen: true,
                        showErrorMessage: showErrorMessage,
                        submitting: false
                    });
                });
            });
        });
    };

    /**
     * Create table rows of Transfer Audit items
     *
     * @param list
     * @return {*}
     */
    makeLocationAuditTable = (list) => {
        if (!list || !list.length) {
            const styles = {
                columnSmall: {paddingLeft: 10, paddingRight: 10}
            };
            return (
                <TableRow>
                    <TableRowColumn colSpan={3} style={styles.columnSmall}>This Audit has no contents.</TableRowColumn>
                    <TableRowColumn style={styles.columnSmall}>&nbsp;</TableRowColumn>
                </TableRow>
            );
        }
        return (
            list.map((item, index) => (
                <LocationAuditTableRow
                    key={item.audit_uuid.toString() + index}
                    data={item}
                />
            ))
        );
    };

    handleConfirmationClose = () => {
        this.setState({confirmationOpen: false});
    };

    render() {
        const {
            auditData,
            confirmationOpen,
            contentList,
            displayUpdate,
            noContentMessage,
            noContents,
            notificationMessage,
            notificationOpen,
            showErrorMessage,
            showSuccessMessage,
            submitting
        } = this.state;

        const {canEdit} = this.props;

        const styles = {
            buttonCard: {
                flex: '0 1 50%',
                display: 'inline-block',
                width: 'calc(50% - 0.5em)',
                margin: '1em 1em 1em 0',
                verticalAlign: 'top'
            },
            buttonCardLast: {
                marginRight: 0
            },
            dialogContent: {maxWidth: 500, textAlign: 'center'},
            dialogButton: {margin: '0 8px'},
            fieldDescription: {
                display: 'block',
                width: '100%',
                fontSize: '14px',
                color: FuseTheme.palette.lightTextColor,
                margin: '0 0 1em 0'
            },
            fieldError: {
                display: 'block',
                width: '100%',
                fontSize: '14px',
                color: FuseTheme.palette.errorColor,
                lineHeight: 1.3,
                margin: '1em 0 1em 0'
            },
            fieldLink: {
                color: FuseTheme.palette.avatarColor,
            },
            fieldSuccess: {
                margin: '1em 0 0 0'
            },
            inlineBlock: {display: 'inline-block'},
            loader: {
                display: 'block',
                textAlign: 'center',
                margin: '12px auto 0px auto'
            },
            tableCard: {display: 'block', margin: '1em 0 0 0'},
            columnSmall: {paddingLeft: 10, paddingRight: 10},
            iconColumn: {padding: '0 14px'},
            notification: {
                backgroundColor: FuseTheme.palette.primary1Color,
                lineHeight: 1.5,
                height: 'auto'
            }
        };

        return (
            <div>
                <Card style={styles.tableCard} initiallyExpanded>
                    <CardHeader title="Location Audit Contents" actAsExpander={true} showExpandableButton={true}/>
                    <CardText expandable={true}>
                        {noContentMessage &&
                            <span style={styles.fieldDescription}>{noContentMessage}</span>
                        }
                        {!noContents &&
                            <span style={styles.fieldDescription}>
                            Review this Audit's Cases, Retail Boxes, or Repacks below.
                            Toggle open Retail Boxes and Repacks to view their contents.
                                {canEdit &&
                                    <span> If this Audit is approved, click
                            the <strong>Finalize</strong> button.</span>
                                }
                        </span>
                        }
                        {/* Audit Contents Table Header and Table Row */}
                        <Table selectable={false} multiSelectable={false}>
                            {!noContents &&
                                <TableHeader displaySelectAll={false} adjustForCheckbox={false}>
                                    <TableRow>
                                        <TableHeaderColumn colSpan={2}>Barcode</TableHeaderColumn>
                                        <TableHeaderColumn style={styles.columnSmall}>Qty</TableHeaderColumn>
                                        <TableHeaderColumn>Unit Type</TableHeaderColumn>
                                        <TableHeaderColumn style={styles.iconColumn}>View Contents</TableHeaderColumn>
                                    </TableRow>
                                </TableHeader>
                            }
                            <TableBody displayRowCheckbox={false}>
                                {[...this.makeLocationAuditTable(contentList)]}
                            </TableBody>
                        </Table>
                        {/* Update messaging and button */}
                        {displayUpdate &&
                            <Card style={styles.buttonCard}>
                                <CardText>
                                    {!noContents && canEdit &&
                                        <p style={styles.fieldDescription}>If there are discrepancies between the above
                                            Audit
                                            and
                                            this Location's inventory, update the quantities and
                                            click <strong>Update Audit</strong>.</p>
                                    }
                                    <RaisedButton
                                        primary
                                        label="Update Audit"
                                        icon={<ContentChange/>}
                                        onClick={this.handleUpdateAudit}
                                        disabled={false}
                                    />
                                </CardText>
                            </Card>
                        }
                        {/* Finalize messaging and buttons */}
                        <Card style={{...styles.buttonCard, ...styles.buttonCardLast}}>
                            {!noContents && canEdit &&
                                <CardText>
                                    {auditData && auditData.type === 'normal' &&
                                        <div>
                                            <p style={styles.fieldDescription}>
                                                If this Audit is approved, click <strong>Finalize Audit</strong>.
                                            </p>
                                            <RaisedButton
                                                primary
                                                label="Finalize Audit"
                                                icon={<ContentCheck/>}
                                                onClick={this.handleShowConfirm}
                                                disabled={submitting || showSuccessMessage || (notificationMessage !== '') || confirmationOpen}
                                            />
                                            {submitting &&
                                                <div>
                                                    <br/>
                                                    <p style={styles.fieldDescription}>
                                                        Processing; please do not leave this page...
                                                    </p>
                                                    <CircularProgress
                                                        size={20}
                                                        thickness={2}
                                                        style={styles.loader}
                                                    />
                                                </div>
                                            }
                                            {showErrorMessage &&
                                                <p style={styles.fieldError}>
                                                    The Audit may or may not have been finalized. Please
                                                    return to <strong>
                                                    <a href="#/LocationAudits" style={styles.fieldLink}>Location
                                                        Audits</a>
                                                </strong> to check if the Audit is still listed.
                                                </p>
                                            }
                                        </div>
                                    }
                                    {auditData && auditData.type === 'final' &&
                                        <div>
                                            <p style={styles.fieldDescription}>
                                                If this Audit is approved and this Location is ready to be closed,
                                                click <strong>Finalize Audit and Close Location</strong>.
                                            </p>
                                            <RaisedButton
                                                primary
                                                label="Finalize Audit and Close Location"
                                                icon={<ContentCheck/>}
                                                onClick={this.handleShowConfirm}
                                                disabled={submitting || showSuccessMessage || (notificationMessage !== '') || confirmationOpen}
                                            />
                                            {submitting &&
                                                <div>
                                                    <br/>
                                                    <p style={styles.fieldDescription}>
                                                        Processing; please do not leave this page...
                                                    </p>
                                                    <CircularProgress
                                                        size={20}
                                                        thickness={2}
                                                        style={styles.loader}
                                                    />
                                                </div>
                                            }
                                            {showErrorMessage &&
                                                <p style={styles.fieldError}>
                                                    The Audit may or may not have been finalized. Please
                                                    return to <strong>
                                                    <a href="#/LocationAudits" style={styles.fieldLink}>Location
                                                        Audits</a>
                                                </strong> to check if the Audit is still listed.
                                                </p>
                                            }
                                        </div>
                                    }
                                    {showSuccessMessage &&
                                        <p style={{...styles.fieldDescription, ...styles.fieldSuccess}}>
                                            This audit has been completed.
                                        </p>
                                    }
                                </CardText>
                            }
                        </Card>
                    </CardText>
                </Card>
                {/* Confirmation Dialog */}
                <Dialog
                    modal={false}
                    open={confirmationOpen}
                    onRequestClose={this.handleConfirmationClose}
                    contentStyle={styles.dialogContent}
                >
                    <div style={styles.dialogContent}>
                        {auditData && auditData.type === 'normal' &&
                            <div>
                                <p style={styles.fieldDescription}>
                                    Are you sure you want to <strong>Finalize Audit</strong> for
                                    this Location?
                                </p>
                                <p style={styles.fieldDescription}>
                                    Once submitted, the process of finalizing the audit may take some time.
                                    Please do not leave the page while finalizing.
                                </p>
                                <div>
                                    <RaisedButton
                                        type="button"
                                        primary
                                        onClick={this.handleFinalizeAudit}
                                        disabled={submitting}
                                        label="Finalize Audit"
                                        style={styles.dialogButton}
                                    />
                                    <FlatButton
                                        type="button"
                                        primary
                                        onClick={this.handleConfirmationClose}
                                        label="Cancel"
                                        style={styles.dialogButton}
                                    />
                                </div>
                            </div>
                        }
                        {auditData && auditData.type === 'final' &&
                            <div>
                                <p style={styles.fieldDescription}>
                                    Are you sure you want
                                    to <strong>Finalize Audit</strong> and <strong>Close this Location</strong>?
                                </p>
                                <p style={styles.fieldDescription}>
                                    Once submitted, the process of finalizing the audit and closing the Location may
                                    take some time. Please do not leave the page while processing.
                                </p>
                                <div>
                                    <RaisedButton
                                        type="button"
                                        primary
                                        onClick={this.handleFinalizeAudit}
                                        disabled={submitting}
                                        label="Finalize Audit and Close Location"
                                        style={styles.dialogButton}
                                    />
                                    <FlatButton
                                        type="button"
                                        primary
                                        onClick={this.handleConfirmationClose}
                                        label="Cancel"
                                        style={styles.dialogButton}
                                    />
                                </div>
                            </div>
                        }
                    </div>
                </Dialog>
                {/* Finalize Notification */}
                <Snackbar
                    open={notificationOpen}
                    message={notificationMessage}
                    autoHideDuration={5000}
                    bodyStyle={styles.notification}
                    style={{bottom: 10}}
                />
            </div>
        );
    }
}

LocationAuditTable.propTypes = {
    addField: PropTypes.bool,
    canEdit: PropTypes.bool,
    listUrl: PropTypes.string.isRequired,
    history: PropTypes.object,
    record: PropTypes.object
};

LocationAuditTable.defaultProps = {
    addField: true,
    listUrl: '/LocationAudits'
};

export default LocationAuditTable;



// WEBPACK FOOTER //
// ./src/Fuse/components/LocationAuditTable.js